/* -------------------------------------------------------------
 * AETHER THEME — by Hamza Taj (linkedin.com/in/hamzataj) - GLOBAL JAVASCRIPT
 * ------------------------------------------------------------- */

// ==========================================================================
// 1. Performance Utilities (Core Web Vitals & INP Optimization)
// ==========================================================================

/**
 * Polyfill-like helper to yield to the main thread to prevent blocking long tasks.
 * Helps improve INP (Interaction to Next Paint).
 */
export async function yieldToMain() {
  if ('scheduler' in window && 'yield' in scheduler) {
    return await scheduler.yield();
  }
  return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * Debounce helper to limit function execution frequency.
 */
export function debounce(func, wait) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

/**
 * Throttle helper for scroll/resize triggers.
 */
export function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// ==========================================================================
// 2. Dynamic Asset Loader
// ==========================================================================
const loadedAssets = new Set();

/**
 * Dynamic script loader to load scripts on demand.
 */
export function loadScript(src) {
  if (loadedAssets.has(src)) return Promise.resolve();
  
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.type = 'module';
    script.onload = () => {
      loadedAssets.add(src);
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

/**
 * Dynamic stylesheet loader.
 */
export function loadStyle(href) {
  if (loadedAssets.has(href)) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => {
      loadedAssets.add(href);
      resolve();
    };
    link.onerror = reject;
    document.head.appendChild(link);
  });
}

// ==========================================================================
// 3. Accessibility & Focus Traps
// ==========================================================================

/**
 * Safe focus trapping logic inside container elements (drawers/modals)
 */
export function trapFocus(container) {
  const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  
  container.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    
    const focusable = Array.from(container.querySelectorAll(focusableSelectors))
      .filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
      
    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }
    
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    
    if (e.shiftKey) {
      if (document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  });
}

/**
 * Set elements container interactive status. Uses modern `inert` attribute.
 */
export function setPageInert(inertState) {
  const mainContent = document.getElementById('MainContent');
  const footerContent = document.querySelector('footer');
  const headerContent = document.querySelector('header');
  
  if (mainContent) mainContent.inert = inertState;
  if (footerContent) footerContent.inert = inertState;
  // Note: Keep drawer container itself non-inert!
}

// ==========================================================================
// 4. Scroll Animations (Intersection Observer)
// ==========================================================================
const animationObserver = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.classList.add('is-visible');
      animationObserver.unobserve(entry.target);
    }
  });
}, {
  root: null, // Viewport
  rootMargin: '0px 0px -50px 0px', // Trigger slightly before entering fully
  threshold: 0.1
});

/**
 * Initializes scroll animations on child elements of a container.
 */
const REVEAL_SELECTOR = '.animate-fade-in, .animate-on-scroll';

export function initAnimations(container = document.body) {
  if (!container) return;
  const isRevealRoot = container.classList &&
    (container.classList.contains('animate-fade-in') || container.classList.contains('animate-on-scroll'));
  const elements = isRevealRoot
    ? [container, ...container.querySelectorAll(REVEAL_SELECTOR)]
    : container.querySelectorAll(REVEAL_SELECTOR);

  elements.forEach(element => {
    animationObserver.observe(element);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initAnimations(document.body);
});

// Re-run when a section is re-rendered in the Theme Editor.
document.addEventListener('shopify:section:load', (event) => {
  initAnimations(event.target);
});

// ==========================================================================
// 6. Prefetch-on-intent — near-instant navigation (Core Web Vitals / UX)
// ==========================================================================
/**
 * Prefetches an internal page when the user signals intent (hover / touchstart /
 * focus), so the next navigation is warm. Respects Save-Data and only fires once
 * per URL. This is what gives sub-Horizon perceived navigation speed without a
 * heavyweight SPA/PJAX layer.
 */
const prefetched = new Set();

function shouldPrefetch(anchor) {
  if (!anchor || !anchor.href) return false;
  if (anchor.origin !== window.location.origin) return false;
  if (anchor.hasAttribute('download') || anchor.target === '_blank') return false;
  if (/\/(cart|checkout|account)(\/|$|\?)/.test(anchor.pathname)) return false;
  if (anchor.pathname === window.location.pathname) return false;
  return !prefetched.has(anchor.href);
}

function prefetch(url) {
  prefetched.add(url);
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = url;
  link.as = 'document';
  document.head.appendChild(link);
}

function onIntent(event) {
  const anchor = event.target.closest && event.target.closest('a[href]');
  if (shouldPrefetch(anchor)) prefetch(anchor.href);
}

if (!navigator.connection || !navigator.connection.saveData) {
  const opts = { capture: true, passive: true };
  document.addEventListener('pointerenter', onIntent, opts);
  document.addEventListener('touchstart', onIntent, opts);
  document.addEventListener('focusin', onIntent, opts);
}

// ==========================================================================
// 5. Custom Web Component Base - Safe Custom Element Registration
// ==========================================================================
export function registerCustomElement(tagName, elementClass) {
  if (!customElements.get(tagName)) {
    customElements.define(tagName, elementClass);
  }
}
